import env from "../config/environment";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = env.DEV;
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  private formatMessage(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      error,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error): void {
    if (!this.shouldLog(level)) return;

    const entry = this.formatMessage(level, message, context, error);
    if (this.isDevelopment) {
      const levelNames = ["DEBUG", "INFO", "WARN", "ERROR"];
      const prefix = `[${levelNames[level]}]`;
      if (level === LogLevel.ERROR && error) {
        console.error(prefix, message, context, error);
      } else if (level === LogLevel.WARN) {
        console.warn(prefix, message, context);
      } else {
        console.debug(prefix, message, context);
      }
    } else {
      this.sendToLoggingService(entry);
    }
  }

  private sendToLoggingService(entry: LogEntry): void {
    try {
      const logs = JSON.parse(localStorage.getItem("app_logs") || "[]");
      logs.push(entry);
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      localStorage.setItem("app_logs", JSON.stringify(logs));
    } catch {
      // silent in production
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: Record<string, unknown>, error?: Error): void {
    this.log(LogLevel.WARN, message, context, error);
  }

  error(message: string, context?: Record<string, unknown>, error?: Error): void {
    this.log(LogLevel.ERROR, message, context, error);
  }

  apiCall(endpoint: string, method: string, success: boolean, duration?: number): void {
    this.info(`API ${method} ${endpoint}`, {
      endpoint,
      method,
      success,
      duration: duration ? `${duration}ms` : undefined,
    });
  }

  apiError(endpoint: string, method: string, error: Error): void {
    this.error(`API ${method} ${endpoint} failed`, { endpoint, method }, error);
  }

  performance(operation: string, duration: number, context?: Record<string, unknown>): void {
    this.info(`Performance: ${operation}`, {
      operation,
      duration: `${duration}ms`,
      ...context,
    });
  }

  userAction(action: string, context?: Record<string, unknown>): void {
    this.info(`User action: ${action}`, context);
  }

  getStoredLogs(): LogEntry[] {
    try {
      return JSON.parse(localStorage.getItem("app_logs") || "[]");
    } catch {
      return [];
    }
  }

  clearStoredLogs(): void {
    localStorage.removeItem("app_logs");
  }
}

export const logger = new Logger();

export const log = {
  debug: (message: string, context?: Record<string, unknown>) => logger.debug(message, context),
  info: (message: string, context?: Record<string, unknown>) => logger.info(message, context),
  warn: (message: string, context?: Record<string, unknown>, error?: Error) => logger.warn(message, context, error),
  error: (message: string, context?: Record<string, unknown>, error?: Error) => logger.error(message, context, error),
  api: {
    call: (endpoint: string, method: string, success: boolean, duration?: number) => logger.apiCall(endpoint, method, success, duration),
    error: (endpoint: string, method: string, error: Error) => logger.apiError(endpoint, method, error),
  },
  performance: (operation: string, duration: number, context?: Record<string, unknown>) => logger.performance(operation, duration, context),
  userAction: (action: string, context?: Record<string, unknown>) => logger.userAction(action, context),
};

export default logger;

/** Shim: prefer `getLogger` from `@/lib/utils` in new code. */
export { getLogger } from "@/lib/utils/logger";
